// src/redux/slices/usedContractSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export type UsedContractTxn = {
  date: string;
  detail: string;
  credit: string;
  debit: string;
  balance: string;
  shipment_id?: string;
};
export type UsedContractPayload = {
  status: "success" | "error";
  message: string;
  contract_id?: string;
  category_name?: string;
  rate_per_gram?: string;
  assigned_weight?: string;
  assigned_value?: string;
  transactions?: UsedContractTxn[];
};
type RequestArgs = { customer_id: string; contract_id: string };
type State = {
  loading: boolean;
  error: string | null;
  data: UsedContractPayload | null;
};

const initialState: State = { loading: false, error: null, data: null };

const api = axios.create({
  baseURL: "https://my.axlpl.com/messenger/services_v7",
  headers: { Accept: "application/json" },
  timeout: 15000, // 15s
});

export const fetchUsedContract = createAsyncThunk<
  UsedContractPayload,
  RequestArgs,
  { rejectValue: string }
>(
  "usedContract/fetch",
  async ({ customer_id, contract_id }, { rejectWithValue }) => {
    try {
      // Try x-www-form-urlencoded first (often more reliable on PHP backends)
      const body = new URLSearchParams();
      body.set("customer_id", customer_id);
      body.set("contract_id", contract_id);

      console.log("[usedContract] request", { customer_id, contract_id });

      let res = await api.post("/usedContract", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      // If the API expects multipart instead, you can fallback:
      if (!res?.data || typeof res.data !== "object") {
        const fd = new FormData();
        fd.append("customer_id", customer_id);
        fd.append("contract_id", contract_id);
        res = await api.post("/usedContract", fd);
      }

      console.log("[usedContract] response", res.status, res.data);
      const d = res.data as UsedContractPayload;

      if (d?.status !== "success") {
        return rejectWithValue(d?.message || "Failed to fetch used contract");
      }
      return d;
    } catch (e: any) {
      console.error(
        "[usedContract] error",
        e?.response?.status,
        e?.response?.data || e?.message,
      );
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Error",
      );
    }
  },
);

const usedContractSlice = createSlice({
  name: "usedContract",
  initialState,
  reducers: {
    resetUsedContract: (s) => {
      s.loading = false;
      s.error = null;
      s.data = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchUsedContract.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchUsedContract.fulfilled, (s, a) => {
      s.loading = false;
      s.data = a.payload ?? null;
    });
    b.addCase(fetchUsedContract.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload ?? "Failed to fetch used contract";
    });
  },
});

export const { resetUsedContract } = usedContractSlice.actions;
export default usedContractSlice.reducer;
